import * as React from "react";
import * as uuid from "uuid";

import { ActionPrimary, OneTimePasswordInput } from "../../form";

import {
  InitialStateMinimumRequirements_OneTimePassword_Form,
  useOneTimePasswordUserExperienceSecondStepVerifyCode,
  useOneTimePasswordVerifyCodeForm,
} from "../../providers";

import { OneTimePasswordProminentErrorMessageBox } from "../prominent-error-message-box";

export interface OneTimePasswordVerifyCodeFormUiProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_OneTimePassword_Form;
  };
}

export const OneTimePasswordVerifyCodeFormUi: React.FunctionComponent<
  OneTimePasswordVerifyCodeFormUiProps
> = (props) => {
  const { initialState } = props.useInitialState();
  const { one_time_password } = initialState || {};
  const { length } = one_time_password || {};

  const { form, onFormFieldFocus, onFormInputChange, onFormInputBlur } =
    useOneTimePasswordVerifyCodeForm();

  const { handleVerifyCode, handleSendCode } =
    useOneTimePasswordUserExperienceSecondStepVerifyCode();

  const [lastVerifiedValue, setLastVerifiedValue] = React.useState<
    string | null
  >(null);

  const thisEventBase = React.useMemo(() => `${uuid.v4()}`, []);

  const passwordLength = React.useMemo(() => {
    return length || 0;
  }, [length]);

  const handleEventChangeInValidatedOneTimePasswordValue =
    React.useCallback(() => {
      const hasTheValueChangedSinceTheLastVerifiedValue =
        lastVerifiedValue !== form.oneTimePassword;

      if (hasTheValueChangedSinceTheLastVerifiedValue) {
        setLastVerifiedValue(null);
      }

      if (hasTheValueChangedSinceTheLastVerifiedValue) {
        const currentOneTimePasswordValueLength =
          form.oneTimePassword?.length || 0;
        if (
          passwordLength > 0 &&
          currentOneTimePasswordValueLength >= passwordLength
        ) {
          setLastVerifiedValue(form.oneTimePassword!);
          handleVerifyCode();
        }
      }
    }, [lastVerifiedValue, form, passwordLength, handleVerifyCode]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisEventBase}-changeInValidatedOneTimePasswordValue`,
      handleEventChangeInValidatedOneTimePasswordValue
    );
    return () => {
      window.removeEventListener(
        `${thisEventBase}-changeInValidatedOneTimePasswordValue`,
        handleEventChangeInValidatedOneTimePasswordValue
      );
    };
  }, [handleEventChangeInValidatedOneTimePasswordValue]);
  React.useEffect(() => {
    window.dispatchEvent(
      new Event(`${thisEventBase}-changeInValidatedOneTimePasswordValue`)
    );
  }, [thisEventBase, form]);

  const otpInputGuid = React.useMemo(() => {
    return uuid.v4();
  }, []);

  const handleVerify: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        handleVerifyCode();
      },
      [handleVerifyCode]
    );

  const handleSendNewCode: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        handleSendCode();
      },
      [handleSendCode]
    );

  const handleCancelSubmit: React.MouseEventHandler<HTMLFormElement> =
    React.useCallback((event) => {
      event.preventDefault();
    }, []);

  return (
    <form
      className="one-time-password-verify-code"
      onSubmit={handleCancelSubmit}
    >
      <OneTimePasswordInput
        name="oneTimePassword"
        guid={otpInputGuid}
        passwordLength={passwordLength}
        form={form}
        onFocus={onFormFieldFocus}
        onChange={onFormInputChange}
        onBlur={onFormInputBlur}
      />

      <ActionPrimary
        name="verify-code-and-sign-in"
        onClick={handleVerify}
      >{`SIGN IN`}</ActionPrimary>

      <OneTimePasswordProminentErrorMessageBox />

      <button
        className="send-a-new-code"
        onClick={handleSendNewCode}
      >{`Send a new code`}</button>
    </form>
  );
};
